import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoggingService } from '@newroom-connect/library/services';
import { ICornerCoordinates, IScreenCoords } from '@newroom-connect/library/interfaces';
import { MatrixHelper, ITransform } from '@newroom-connect/library/helpers';

export interface ICornersTransformEvent {
  matrix: string;
  width: number;
  height: number;
  corners: ICornerCoordinates;
}

/**
 * Corner Dots Overlay Component for perspective transformation of hotspots.
 *
 * Provides draggable corner dots that allow users to apply perspective transforms
 * to hotspot elements in 360-degree scenes. Uses the MatrixHelper for matrix calculations.
 *
 * @example
 * ```html
 * <nrc-corner-dots-overlay
 *   [width]="hotspotWidth"
 *   [height]="hotspotHeight"
 *   [visible]="editMode"
 *   (transformChanged)="onTransformChanged($event)"
 * />
 * ```
 */
@Component({
  selector: 'nrc-corner-dots-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: 'corner-dots-overlay.component.html',
  styleUrls: ['corner-dots-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CornerDotsOverlayComponent implements OnInit, OnDestroy {
  private readonly logger = inject(LoggingService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);

  @Input({ required: true }) public hotspotElement!: HTMLElement;
  @Input() public width = 200;
  @Input() public height = 150;

  // If provided, overlay will initialize with these corners and skip default rectangle reset
  private hasInitialCorners = false;
  @Input() public set initialCorners(value: ICornerCoordinates | null) {
    // Guard: apply only once per component lifecycle, and never while dragging
    if (!value || this.hasInitialCorners || this.isDragging) {
      return;
    }

    this.cornerPositions = {
      topLeft: { x: value.topLeft.x, y: value.topLeft.y },
      topRight: { x: value.topRight.x, y: value.topRight.y },
      bottomLeft: { x: value.bottomLeft.x, y: value.bottomLeft.y },
      bottomRight: { x: value.bottomRight.x, y: value.bottomRight.y }
    };

    this.hasInitialCorners = true;
    this.cdRef.markForCheck();
  }

  @Output() public transformStart = new EventEmitter<void>();
  @Output() public transformEnd = new EventEmitter<ICornerCoordinates>();
  @Output() public transformChanged = new EventEmitter<string>();
  // Emits detailed metrics on every transform change
  @Output() public transformMetricsChanged = new EventEmitter<ICornersTransformEvent>();

  public cornerPositions: ICornerCoordinates = {
    topLeft: { x: 0, y: 0 },
    topRight: { x: 0, y: 0 },
    bottomLeft: { x: 0, y: 0 },
    bottomRight: { x: 0, y: 0 }
  };

  // Drag state
  private isDragging = false;
  private dragCorner: keyof ICornerCoordinates | null = null;
  private dragStartPosition: { x: number; y: number } | null = null;

  // Event listeners
  private mouseMoveListener?: (event: MouseEvent) => void;
  private mouseUpListener?: (event: MouseEvent) => void;
  private touchMoveListener?: (event: TouchEvent) => void;
  private touchEndListener?: (event: TouchEvent) => void;

  /**
   * Initialize the component and set up default corner positions.
   */
  public ngOnInit(): void {
    // Only reset to rectangle if no initial corners were provided
    if (!this.hasInitialCorners) {
      this.resetCornerPositions();
    }

    this.setupGlobalEventListeners();
  }

  /**
   * Clean up event listeners when component is destroyed.
   */
  public ngOnDestroy(): void {
    this.cleanupGlobalEventListeners();
  }

  /**
   * Reset corner positions to default rectangle.
   */
  public resetCornerPositions(): void {
    this.cornerPositions = {
      topLeft: { x: 0, y: 0 },
      topRight: { x: this.width, y: 0 },
      bottomLeft: { x: 0, y: this.height },
      bottomRight: { x: this.width, y: this.height }
    };
  }

  /**
   * Start dragging a corner dot.
   *
   * @param corner - The corner being dragged.
   * @param event - Mouse or touch event.
   */
  public startDrag(corner: keyof ICornerCoordinates, event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
    this.dragCorner = corner;

    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

    this.dragStartPosition = { x: clientX, y: clientY };

    this.transformStart.emit();

    this.logger.debug('Started dragging corner', { corner, position: this.dragStartPosition });
  }

  /**
   * Handle drag move events.
   *
   * @param clientX - Mouse/touch X coordinate.
   * @param clientY - Mouse/touch Y coordinate.
   */
  private onDragMove(clientX: number, clientY: number): void {
    if (!this.isDragging || !this.dragCorner || !this.dragStartPosition) {
      return;
    }

    // Calculate delta from drag start
    const deltaX = clientX - this.dragStartPosition.x;
    const deltaY = clientY - this.dragStartPosition.y;

    // Update corner position
    const currentCorner = this.cornerPositions[this.dragCorner];
    // Allow dragging beyond the current bounds for flexible transformations
    const newPosition: IScreenCoords = {
      x: currentCorner.x + deltaX,
      y: currentCorner.y + deltaY
    };

    this.cornerPositions[this.dragCorner] = newPosition;

    // Update drag start position for next delta calculation
    this.dragStartPosition = { x: clientX, y: clientY };

    // Calculate and emit transform
    this.calculateAndEmitTransform();

    // Ensure OnPush view updates while using global listeners
    this.cdRef.markForCheck();
  }

  /**
   * Handle drag end events.
   */
  private onDragEnd(): void {
    if (!this.isDragging) {
      return;
    }

    this.isDragging = false;
    this.dragCorner = null;
    this.dragStartPosition = null;

    this.transformEnd.emit(this.cornerPositions);

    this.logger.debug('Ended dragging corner', { finalPositions: this.cornerPositions });

    // Trigger change detection for final state
    this.cdRef.markForCheck();
  }

  /**
   * Calculate matrix transform from current corner positions and emit the result.
   */
  private calculateAndEmitTransform(): void {
    try {
      // Store the current transform to restore later if needed
      const currentTransform = this.hotspotElement.style.transform;

      // Reset element transform to identity to avoid compound transforms
      this.hotspotElement.style.transform = '';

      // Create transform object for MatrixHelper with clean state
      const transformObject: ITransform = {
        element: this.hotspotElement,
        style: '', // Use empty string for clean calculation
        computedStyle: getComputedStyle(this.hotspotElement),
        width: this.width,
        height: this.height,
        useBackfacing: false,
        topLeft: this.cornerPositions.topLeft,
        topRight: this.cornerPositions.topRight,
        bottomLeft: this.cornerPositions.bottomLeft,
        bottomRight: this.cornerPositions.bottomRight
      };

      // Check for transform errors before calculating
      if (MatrixHelper.hasTransformError(transformObject)) {
        this.logger.warn('Invalid transform detected, skipping calculation');
        // Restore original transform on error
        this.hotspotElement.style.transform = currentTransform;
        return;
      }

      // Calculate matrix transform from clean state
      const result = MatrixHelper.transform(transformObject);

      // Apply the calculated transform to the element
      this.hotspotElement.style.transform = result.style;

      // Emit the calculated matrix3d string
      this.transformChanged.emit(result.style);

      // Also compute and emit transform metrics (bounding width/height and corners)
      const xs = [
        this.cornerPositions.topLeft.x,
        this.cornerPositions.topRight.x,
        this.cornerPositions.bottomLeft.x,
        this.cornerPositions.bottomRight.x
      ];
      const ys = [
        this.cornerPositions.topLeft.y,
        this.cornerPositions.topRight.y,
        this.cornerPositions.bottomLeft.y,
        this.cornerPositions.bottomRight.y
      ];
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const newWidth = Math.max(0, maxX - minX);
      const newHeight = Math.max(0, maxY - minY);

      this.transformMetricsChanged.emit({
        matrix: result.style,
        width: newWidth,
        height: newHeight,
        corners: { ...this.cornerPositions }
      });
    } catch (error) {
      this.logger.error('Failed to calculate transform', error as Error);
    }
  }

  /**
   * Set up global event listeners for drag operations.
   */
  private setupGlobalEventListeners(): void {
    // Mouse events
    this.mouseMoveListener = (event: MouseEvent): void => {
      this.zone.run(() => {
        this.onDragMove(event.clientX, event.clientY);
      });
    };

    this.mouseUpListener = (): void => {
      this.zone.run(() => {
        this.onDragEnd();
      });
    };

    // Touch events
    this.touchMoveListener = (event: TouchEvent): void => {
      if (event.touches.length === 1) {
        event.preventDefault();
        const touch = event.touches[0];

        this.zone.run(() => {
          this.onDragMove(touch.clientX, touch.clientY);
        });
      }
    };

    this.touchEndListener = (): void => {
      this.zone.run(() => {
        this.onDragEnd();
      });
    };

    // Add listeners to document
    document.addEventListener('mousemove', this.mouseMoveListener);
    document.addEventListener('mouseup', this.mouseUpListener);
    document.addEventListener('touchmove', this.touchMoveListener, { passive: false });
    document.addEventListener('touchend', this.touchEndListener);
  }

  /**
   * Clean up global event listeners.
   */
  private cleanupGlobalEventListeners(): void {
    if (this.mouseMoveListener) {
      document.removeEventListener('mousemove', this.mouseMoveListener);
    }

    if (this.mouseUpListener) {
      document.removeEventListener('mouseup', this.mouseUpListener);
    }

    if (this.touchMoveListener) {
      document.removeEventListener('touchmove', this.touchMoveListener);
    }

    if (this.touchEndListener) {
      document.removeEventListener('touchend', this.touchEndListener);
    }

    this.mouseMoveListener = undefined;
    this.mouseUpListener = undefined;
    this.touchMoveListener = undefined;
    this.touchEndListener = undefined;
  }
}
